import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, StateToken, Store } from "@ngxs/store";
import { GcpResourcesService } from "./gcp-resources.service";
import {
    ClearGcpResource,
    LoadAllGcpResources,
    LoadDataTableGcpResources,
    SetGcpResource,
    UpdateCountersGcpResources,
    LoadAllGcpCharts,
    LoadGcpChartsFilteredData
} from "./gcp-resources.actions";
import { finalize, forkJoin, tap } from "rxjs";
import { GcpResourcesStateModel, GcpResource, GcpCounterChartsData } from "./gcp-resources.model";
import { PreferencesFiltersState } from "src/app/store/preferences/filters/preferences-filters.state";
import { CompliantRatioService } from "src/app/shared/service/compliant-ratio.service";
import { GcpResourceTypeEnum } from "src/app/shared/enum/gcp-resource-type.enum";
import { ChartComplianceRatioItem } from "src/app/shared/component/chart-compliance-ratio/chart-compliance-ratio.component";
import { ChartComplianceTotalItem } from "src/app/shared/component/chart-compliance-total/chart-compliance-total.component";
import { ChartVersusItem } from "src/app/shared/component/chart-versus/chart-versus.component";
import { DateService } from "src/app/shared/service/date.service";
import { GcpResourceTypeLabelPipe } from "src/app/shared/pipe/gcp-resource-type-label.pipe";
import { ColorEnum } from "src/app/shared/enum/color.enum";
import { ItemTypeEnum } from "src/app/shared/enum/item-type.enum";
import { MetaService } from "src/app/store/meta/meta.service";
import { MetaRequestHelper } from "src/app/store/meta/meta-request.helper";
import { GcpConfig } from "src/app/store/preferences/providers/preferences-providers.model";
import { PreferencesProvidersState } from "src/app/store/preferences/providers/preferences-providers.state";
import { PreferencesChartsState } from "src/app/store/preferences/charts/preferences-charts.state";

export const GCP_RESOURCES_TOKEN: StateToken<GcpResourcesStateModel> = new StateToken('gcpResources');

const GcpResourcesStateModelDefaults: GcpResourcesStateModel = {
    resourcesPerTenant: {},
    compliantRatio: '0%',
    totalItems: '0',
    compliantItems: '0',
    notCompliantItems: '0',
    datatable: [],
    selectedResource: {
        account: '',
        age: 0,
        id: '',
        tfstates: [],
        repositories: [],
        issues: [],
        name: '',
        source: '',
        state: 'ignored',
        type: ''
    },
    chartsDataPerTenant: {},
    chartData: {
        filteredComplianceRatioChartData: {
            values: []
        },
        filteredComplianceTotalChartData: {
            values: []
        },
        filteredVersusChartData: {
            values: []
        }
    }
}

@State<GcpResourcesStateModel>({
    name: GCP_RESOURCES_TOKEN,
    defaults: GcpResourcesStateModelDefaults
})

@Injectable()
export class GcpResourcesState {

    private readonly KEY = GCP_RESOURCES_TOKEN.getName()

    constructor(
        private gcpResourcesService: GcpResourcesService,
        private store: Store,
        private dateService: DateService,
        private gcpResourceTypeLabelPipe: GcpResourceTypeLabelPipe,
        private metaService: MetaService
    ) {
    }

    @Selector([GCP_RESOURCES_TOKEN])
    static getResources(state: GcpResourcesStateModel) {
        return (tenant: string) => state.resourcesPerTenant[tenant]
    }

    @Selector([GCP_RESOURCES_TOKEN])
    static getCompliantRatio(state: GcpResourcesStateModel) {
        return state.compliantRatio
    }

    @Selector([GCP_RESOURCES_TOKEN])
    static getTotalItems(state: GcpResourcesStateModel) {
        return state.totalItems
    }

    @Selector([GCP_RESOURCES_TOKEN])
    static getCompliantItems(state: GcpResourcesStateModel) {
        return state.compliantItems
    }

    @Selector([GCP_RESOURCES_TOKEN])
    static getNotCompliantItems(state: GcpResourcesStateModel) {
        return state.notCompliantItems
    }

    @Selector([GCP_RESOURCES_TOKEN])
    static getDatatable(state: GcpResourcesStateModel) {
        return state.datatable
    }

    @Selector([GCP_RESOURCES_TOKEN])
    static getSelectedResource(state: GcpResourcesStateModel) {
        return state.selectedResource
    }

    @Selector([GCP_RESOURCES_TOKEN])
    static getFilteredComplianceRatioChartData(state: GcpResourcesStateModel) {
        return state.chartData.filteredComplianceRatioChartData
    }

    @Selector()
    static getFilteredComplianceTotalChartData(state: GcpResourcesStateModel) {
        return state.chartData.filteredComplianceTotalChartData
    }

    @Selector()
    static getFilteredVersusChartData(state: GcpResourcesStateModel) {
        return state.chartData.filteredVersusChartData
    }

    @Action(LoadAllGcpResources)
    loadAllGcpResources(ctx: StateContext<GcpResourcesStateModel>) {
        const gcpConfig = this.store.selectSnapshot(PreferencesProvidersState.getGcpConfig);
        const organizationIds = gcpConfig.preferences && gcpConfig.preferences.length > 0
            ? gcpConfig.preferences
            : gcpConfig.organizations.map((o: any) => o.organization_id);

        if (organizationIds.length === 0) return;

        const staleOrganizationIds = organizationIds.filter((id: string) => this.metaService.isStale(this.KEY + "-tenant-" + id));

        if (staleOrganizationIds.length === 0) return ctx.dispatch(new LoadDataTableGcpResources());

        return forkJoin(
            staleOrganizationIds.map((organizationId: string) =>
                MetaRequestHelper.wrapRequest(
                    this.store,
                    this.KEY + "-tenant-" + organizationId,
                    this.gcpResourcesService.loadGcpResources(organizationId),
                    resources => {
                        ctx.patchState({
                            resourcesPerTenant: {
                                ...ctx.getState().resourcesPerTenant,
                                [organizationId]: resources
                            }
                        })
                    }
                )
            )
        ).pipe(
            finalize(() => ctx.dispatch(new LoadDataTableGcpResources()))
        );
    }

    @Action(LoadDataTableGcpResources)
    loadDataTableGcpResources(ctx: StateContext<GcpResourcesStateModel>) {
        const state = ctx.getState()
        let datatable_resources: GcpResource[] = []

        for (const [key, resources] of Object.entries(state.resourcesPerTenant)) {
            resources.forEach((resource) => {
                datatable_resources.push(resource)
            })
        }

        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.GCP_RESOURCES]]
        filters.forEach(filter => {
            if (filter.key == 'issuesCount') {
                datatable_resources = datatable_resources.filter((resource) => resource['issues'].length.toString() === filter.value)
            } else if (filter.key == 'issuesType') {
                datatable_resources = datatable_resources.filter((resource) => {
                    let issuesTypeFound = false
                    resource.issues.forEach(issue => {
                        issuesTypeFound = issuesTypeFound || filter.value.includes(issue.issue_type)
                    })
                    return issuesTypeFound
                })
            } else if (filter.key == 'age') {
                let unknownAge = 99999
                let filterAge: number = unknownAge
                if (filter.value == 'today') {
                    filterAge = 0
                } else if (filter.value.startsWith('last_')) {
                    filterAge = filter.value.substring(5, 7)
                }
                datatable_resources = datatable_resources.filter((resource) => {
                    let ageInRange = false
                    if (filterAge == unknownAge) {
                        ageInRange = resource.age == filterAge
                    } else {
                        ageInRange = resource.age <= filterAge
                    }
                    return ageInRange
                })
            } else {
                if (Array.isArray(filter.value)) {
                    datatable_resources = datatable_resources.filter((resource) => filter.value.includes(resource[filter.key as keyof GcpResource]))
                } else {
                    datatable_resources = datatable_resources.filter((resource) => resource[filter.key as keyof GcpResource] === filter.value)
                }
            }
        });
        ctx.patchState({
            datatable: datatable_resources
        })
        ctx.dispatch(new UpdateCountersGcpResources())
    }

    @Action(UpdateCountersGcpResources)
    updateCountersGcpResources(ctx: StateContext<GcpResourcesStateModel>) {
        const state = ctx.getState()
        let totalItemsCounter = 0
        let compliantItemsCounter = 0
        let notCompliantItemsCounter = 0
        state.datatable.forEach(resource => {
            if (resource.state == 'compliant') {
                totalItemsCounter++;
                compliantItemsCounter++;
            } else if (resource.state == 'not_compliant') {
                totalItemsCounter++;
                notCompliantItemsCounter++;
            }
        });
        ctx.patchState({
            compliantRatio: CompliantRatioService.calculateCompliantRatio(compliantItemsCounter, notCompliantItemsCounter),
            totalItems: '' + totalItemsCounter,
            compliantItems: '' + compliantItemsCounter,
            notCompliantItems: '' + notCompliantItemsCounter
        })
    }

    @Action(ClearGcpResource)
    clearGcpResource(ctx: StateContext<GcpResourcesStateModel>) {
        return ctx.patchState({
            selectedResource: GcpResourcesStateModelDefaults.selectedResource
        })
    }

    @Action(SetGcpResource)
    setGcpResource(ctx: StateContext<GcpResourcesStateModel>, { resource_organization, resource_id }: SetGcpResource) {
        return this.gcpResourcesService.loadGcpResourceDetails(resource_organization, resource_id)
            .pipe(
                tap(value => {
                    return ctx.patchState({
                        selectedResource: value
                    })
                }),
            )
    }

    @Action(LoadAllGcpCharts)
    loadAllGcpCharts(ctx: StateContext<GcpResourcesStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig);
        const { start_date, end_date } = DateService.getChartsMeasuresDateRange(chartsConfig);
        const gcpConfig = this.store.selectSnapshot(PreferencesProvidersState.getGcpConfig);
        const organizationIds = gcpConfig.preferences && gcpConfig.preferences.length > 0
            ? gcpConfig.preferences
            : gcpConfig.organizations.map((o: any) => o.organization_id);

        if (organizationIds.length === 0) return;

        const staleOrganizationIds = organizationIds.filter((id: string) => this.metaService.isStale(this.KEY + "-charts-tenant-" + id + "-" + start_date + "-" + end_date + "-" + chartsConfig.granularity));

        if (staleOrganizationIds.length === 0) return ctx.dispatch(new LoadGcpChartsFilteredData());

        return forkJoin(
            staleOrganizationIds.map((organizationId: string) =>
                MetaRequestHelper.wrapRequest(
                    this.store,
                    this.KEY + "-charts-tenant-" + organizationId + "-" + start_date + "-" + end_date + "-" + chartsConfig.granularity,
                    this.gcpResourcesService.loadGcpMeasures(organizationId, start_date, end_date, chartsConfig.granularity),
                    charts_payload => {
                        ctx.patchState({
                            chartsDataPerTenant: {
                                ...ctx.getState().chartsDataPerTenant,
                                [organizationId]: charts_payload.measures
                            }
                        })
                    }
                )
            )
        ).pipe(
            finalize(() => ctx.dispatch(new LoadGcpChartsFilteredData()))
        );
    }

    mergeCountersByDate(input: { [org: string]: GcpCounterChartsData }): GcpCounterChartsData {
        const result: GcpCounterChartsData = {};

        for (const orgId of Object.keys(input)) {
            const dates = input[orgId];

            for (const dateKey of Object.keys(dates)) {
                const types = dates[dateKey];

                if (!result[dateKey]) {
                    result[dateKey] = {};
                }

                for (const typeKey of Object.keys(types)) {
                    if (!result[dateKey][typeKey]) {
                        result[dateKey][typeKey] = {
                            checked: 0,
                            unchecked: 0,
                            ignored: 0
                        };
                    }

                    result[dateKey][typeKey].checked += types[typeKey].checked;
                    result[dateKey][typeKey].unchecked += types[typeKey].unchecked;
                    result[dateKey][typeKey].ignored += types[typeKey].ignored;
                }
            }
        }

        return result;
    }

    @Action(LoadGcpChartsFilteredData)
    loadGcpChartsFilteredData(ctx: StateContext<GcpResourcesStateModel>) {
        const state = ctx.getState()

        let complianceRatioChartValues: ChartComplianceRatioItem[] = [];
        let complianceTotalChartValues: ChartComplianceTotalItem[] = [];
        let complianceVersusChartValues: ChartVersusItem[] = [];
        let filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.GCP_MEASURES]]
        let resource_types: string[] = []
        let organization_ids: string[] = []
        filters.forEach(filter => {
            if (filter.key === 'type') {
                resource_types = filter.value
            } else if (filter.key === 'organization') {
                organization_ids = filter.value
            }
        })
        if (resource_types.length == 0) {
            resource_types = Object.keys(GcpResourceTypeEnum)
        }

        let counterChartsData: GcpCounterChartsData = {}

        // Filter chartsDataPerTenant by organization filter or use all organizations
        let filteredChartsDataPerOrg: Record<string, GcpCounterChartsData> = {}
        if (organization_ids.length > 0) {
            for (const org_id of organization_ids) {
                if (state.chartsDataPerTenant[org_id]) {
                    filteredChartsDataPerOrg[org_id] = state.chartsDataPerTenant[org_id]
                }
            }
        } else {
            filteredChartsDataPerOrg = state.chartsDataPerTenant
        }
        counterChartsData = this.mergeCountersByDate(filteredChartsDataPerOrg)

        for (const [dateString, charts_data] of Object.entries(counterChartsData)) {
            let formattedDate = this.dateService.formatLongDate(dateString)
            let compliantCount = 0
            let notCompliantCount = 0
            for (const type in charts_data) {
                if (resource_types.includes(type)) {
                    const typeMeasures = charts_data[type];
                    compliantCount += typeMeasures.checked;
                    notCompliantCount += typeMeasures.unchecked;
                }
            }
            complianceRatioChartValues.push({
                "dateString": formattedDate,
                "date": new Date(formattedDate),
                "value": CompliantRatioService.calculateCompliantRatio(compliantCount, notCompliantCount)
            });
            complianceTotalChartValues.push({
                "dateString": formattedDate,
                "date": new Date(formattedDate),
                "compliantCount": compliantCount,
                "notCompliantCount": notCompliantCount,
                "total": compliantCount + notCompliantCount,
                "delta": Math.abs(compliantCount - notCompliantCount)
            });
        }
        let lastDate = Object.keys(counterChartsData)[Object.keys(counterChartsData).length - 1]
        let lastMeasure = counterChartsData[lastDate]
        for (const type in lastMeasure) {
            if (resource_types.includes(type)) {
                let typeMeasure = lastMeasure[type]
                let colorKey = 'charts_color_' + complianceVersusChartValues.length
                let dateData = {
                    "type": this.gcpResourceTypeLabelPipe.transform(type),
                    "count": typeMeasure.checked + typeMeasure.unchecked + typeMeasure.ignored,
                    "color": ColorEnum[colorKey as keyof typeof ColorEnum]
                };
                complianceVersusChartValues.push(dateData);
            }
        }
        return ctx.patchState(
            {
                chartData: {
                    filteredComplianceRatioChartData: {values :complianceRatioChartValues},
                    filteredComplianceTotalChartData: {values :complianceTotalChartValues},
                    filteredVersusChartData: {values :complianceVersusChartValues}
                }
            }
        )
    }
}
